"use client";

import {
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  Sku,
} from "@chipi-stack/types";
import { useChipiContext } from "../context";
import { ChipiApiError, ErrorResponse } from "@chipi-stack/shared";

type GetSkuInput = {
  id: string;
  getBearerToken?: () => Promise<string | null | undefined>;
  queryOptions?: Omit<
  UseQueryOptions<Sku, Error>,
  "queryKey" | "queryFn"
>;
};

export function useGetSku(input?: GetSkuInput) {
  const { chipiSDK } = useChipiContext();
  const queryClient = useQueryClient();

  const query = useQuery<
    Sku,
    Error
  >({
    queryKey: ["sku", input?.id],
    queryFn: async () => {
      if (!input?.id || !input?.getBearerToken)
        throw new Error("id and getBearerToken are required");
      const bearerToken = await input.getBearerToken();
      if (!bearerToken) throw new Error("Bearer token is required");
      return chipiSDK.getSku(input.id, bearerToken);
    },
    enabled: Boolean(
      input?.id && 
      input?.getBearerToken &&
      input.id.trim() !== ""
    ),
    retry: (failureCount: number, error: Error) => {
      // Don't retry on API errors (errors with status codes like 404, 400, etc.)
      if (error instanceof ChipiApiError || (error as any)?.status) {
        return false;
      }
      // Retry on network errors (no status code) up to 3 times
      return failureCount < 3;
    },
    ...input?.queryOptions,
  });

  const fetchSku = async (input: GetSkuInput): Promise<Sku> => {
    return queryClient.fetchQuery({
      queryKey: ["sku", input?.id],
      queryFn: async () => {
        if (!input?.getBearerToken) throw new Error("getBearerToken is required");
        const bearerToken = await input.getBearerToken();
        if (!bearerToken) throw new Error("Bearer token is required");
        return chipiSDK.getSku(input.id, bearerToken);
      },
      retry: (failureCount, error) => {
        // Don't retry on API errors (errors with status codes like 404, 400, etc.)
        if (error instanceof ChipiApiError || (error as ErrorResponse)?.code) {
          return false;
        }
        // Retry on network errors (no status code) up to 3 times
        return failureCount < 3;
      },
    });
  };

  return {
    ...query,
    fetchSku,
  };
}